'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DndContext } from '@dnd-kit/core';
import {
  ReactFlow,
  Background,
  MiniMap,
  ReactFlowProvider,
  SelectionMode,
  type OnSelectionChangeParams,
  useReactFlow,
  useStoreApi,
} from '@xyflow/react';

import { createLogger } from '@core/be/platform/logging/Logger';
import '@xyflow/react/dist/style.css';

import type { Mindmap } from '@/features/mindmap/core/MindmapTypes';
import type { MindmapNodeData } from '@/features/mindmap/core/MindmapTransform';
import { buildReactFlowEdges, buildReactFlowNodes } from '@/features/mindmap/core/MindmapTransform';
import { computeMindmapLayoutAsync } from '@/features/mindmap/core/MindmapLayout';
import {
  getInitialUIState,
  getInteractionFlags,
  toggleMode,
  createChildLast,
  createChildFirst,
  createSiblingAfter,
  createSiblingBefore,
  renameNode,
  getParentIdOf,
  deleteSubtree,
  toggleNodeCollapse,
  moveNodeWithinParent,
  moveNodeToParent,
} from '@/features/mindmap/core/MindmapState';
import { createMindmapSaver, loadMindmap } from '@/features/mindmap/core/MindmapPersistence';
import { loadMindmapViewport, saveMindmapViewport, saveMindmapCollapsed } from '@/features/mindmap/core/MindmapMulti';
import { getSeedMindmap } from '@/features/mindmap/core/MindmapState';
import { KeyboardShortcutsLegend } from './KeyboardShortcutsLegend';
import { MindmapSwitcher } from './MindmapSwitcher';
import { MindmapInfo } from './MindmapInfo';
import { MindmapLasso } from './MindmapLasso';
import { useMindmapLasso } from './hooks/UseMindmapLasso';
import { useMindmapToolbar } from './hooks/UseMindmapToolbar';
import { useMindmapClipboard } from './hooks/UseMindmapClipboard';
import { useKeyboardMindmapControls } from './hooks/UseKeyboardMindmapControls';
import { useMindmapHistory } from './hooks/UseMindmapHistory';
import { useMindmapIO } from './hooks/UseMindmapIo';
import { useMindmapLayout } from './hooks/UseMindmapLayout';
import { MindmapControls } from './MindmapControls';
import MindmapNode, { RuntimeMindmapNodeData } from './MindmapNode';
import { MindmapCanvasContextMenu } from './MindmapContextMenus';
import { getSiblingsSortedByPosition as getSiblingsSortedByPositionUtil } from './utils/sibling-ordering';
import { AudioTranscriptionEventBus } from '@audio-transcription';
import NodeStyleMenubar from './NodeStyleMenubar';
import { useMindmapDragAndDrop } from './drag-and-drop/UseMindmapDragAndDrop';
import { DragFeedback, dragFeedbackStyles } from './drag-and-drop/MindmapDragFeedback';
import { LuCheck, LuClock } from 'react-icons/lu';

export { resolveActiveMindmap } from './hooks/UseMindmapLayout';

export interface MindmapCanvasProps {
  mindmap?: Mindmap | null;
}

interface MindmapCanvasInnerProps {
  mindmap: Mindmap;
}

// Temporary: disable toolbar gating during viewport pan/drag without removing logic
const DISABLE_TOOLBAR_PAN_GATING = true;

const nodeTypes = { mindmapNode: MindmapNode } as any;

const toRawId = (rfId: string) => (rfId.includes('::') ? rfId.split('::').pop()! : rfId);

const areArraysEqual = (a: readonly string[], b: readonly string[]) => {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
};

function MindmapCanvasInner({ mindmap }: MindmapCanvasInnerProps) {
  const { doc, setDoc, setDocWithHistory, undo, redo, resetHistory } = useMindmapHistory({
    initialMindmap: mindmap,
  });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [pendingEditText, setPendingEditText] = useState<string | null>(null);
  const [selectedRawIds, setSelectedRawIds] = useState<string[]>([mindmap.rootId]);
  const [uiState, setUiState] = useState(getInitialUIState());
  const [panGuardActive, setPanGuardActive] = useState(false);
  const [saveState, setSaveState] = useState<'saved' | 'dirty'>('saved');

  const reactFlow = useReactFlow();
  const storeApi = useStoreApi();
  const loggerRef = useRef(createLogger('mindmap:canvas'));
  const wheelLoggerRef = useRef(createLogger('mindmap:canvas-wheel'));
  const renameLoggerRef = useRef(createLogger('mindmap:canvas-rename'));
  const perfLoggerRef = useRef(createLogger('mindmap:performance'));
  const logger = loggerRef.current;
  const wheelLogger = wheelLoggerRef.current;
  const renameLogger = renameLoggerRef.current;
  const perfLogger = perfLoggerRef.current;
  const dndLoggerRef = useRef(createLogger('mindmap:dnd'));
  const dragUiLoggerRef = useRef(createLogger('mindmap:drag-ui'));
  const dragPreviewLoggerRef = useRef(createLogger('mindmap:drag-preview'));

  // Track renders during pan/zoom
  const renderCountRef = useRef(0);
  const lastRenderTimeRef = useRef(performance.now());
  useEffect(() => {
    renderCountRef.current++;
    const now = performance.now();
    const timeSinceLastRender = now - lastRenderTimeRef.current;

    if (viewportInteractingRef.current && timeSinceLastRender < 100) {
      perfLogger.warn(`🔴 RENDER during pan/zoom #${renderCountRef.current}`, {
        timeSinceLastMs: timeSinceLastRender.toFixed(2),
        nodeCount: baseNodes.length,
      });
    }
    lastRenderTimeRef.current = now;
  });
  const saverRef = useRef(
    createMindmapSaver(1000, {
      onSchedule: () => setSaveState('dirty'),
      onSaved: () => setSaveState('saved'),
    })
  );
  const cancelEditRef = useRef(false);
  const pendingSelectionRef = useRef<string[] | null>([mindmap.rootId]);
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const viewportInteractingRef = useRef(false);
  const viewportInteractionStartTimeRef = useRef<number>(0);
  const viewportZoomRef = useRef<number>(1);
  const didInitialFitRef = useRef(false);
  const lastKeyRef = useRef<{ code: string; ts: number }>({ code: '', ts: 0 });
  const selectedRawIdsRef = useRef<string[]>(selectedRawIds);
  const editingInputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const genId = useCallback((): string => {
    try {
      // @ts-ignore
      if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    } catch {}
    return 'n-' + Math.random().toString(36).slice(2, 10);
  }, []);
  const editingInputIdRef = useRef<string | null>(null);
  const editingSelectionModeRef = useRef<'select-all' | 'caret-end'>('caret-end');
  // Undo/redo history stacks
  const {
    toolbarNode,
    hoveredNodeId,
    previewMindmap: previewDoc,
    layoutPreview,
    hideToolbar,
    clearHoveredNode,
    handleNodeHoverChange,
    handleStylePreview,
    handleStyleCommit,
    handleDirectionPreview,
    handleDirectionCommit,
    handleDirectionReset,
  } = useMindmapToolbar({
    doc,
    editingId,
    selectedRawIds,
    selectedRawIdsRef,
    viewportInteractingRef,
    setDocWithHistory,
    disableToolbarPanGating: DISABLE_TOOLBAR_PAN_GATING,
  });

  const { activeMindmap, baseNodes, positions, nodeDimensions, setNodeDimensions, exitingNodes, resetLayoutState } =
    useMindmapLayout({
      doc,
      previewMindmap: previewDoc,
      layoutPreview,
      editingId,
      renameLogger,
      dragPreview: null,
    });

  const docRef = useRef(doc);
  useEffect(() => {
    docRef.current = doc;
  }, [doc]);

  const baselineDocRef = useRef<Mindmap | null>(null);
  const previewDocRef = useRef<Mindmap | null>(null);
  const skipPreviewRef = useRef(false);
  const previewInProgressRef = useRef(false);
  const queuedPreviewRef = useRef<{ draggedId: string; parentId: string | null; index: number } | null>(null);

  // Track positions and dimensions for drag controller
  const positionsRef = useRef<Record<string, { x: number; y: number }>>({});
  const nodeDimensionsRef = useRef<Record<string, { width: number; height: number }>>({});

  const buildMovedDoc = useCallback(
    (
      source: Mindmap | null,
      draggedId: string,
      targetParentId: string | null,
      targetIndex: number,
      asGhost: boolean
    ) => {
      const base = source ?? docRef.current;
      if (!base) return docRef.current;
      const node = base.nodes.find((n) => n.id === draggedId);
      if (!node) return base;

      const result =
        node.parentId === targetParentId
          ? moveNodeWithinParent(base, draggedId, targetIndex).mindmap
          : moveNodeToParent(base, draggedId, targetParentId, targetIndex).mindmap;

      if (asGhost) {
        return {
          ...result,
          nodes: result.nodes.map((n) => (n.id === draggedId ? { ...n, isGhost: true } : n)),
        };
      }

      return {
        ...result,
        nodes: result.nodes.map((n) => (n.id === draggedId ? { ...n, isGhost: undefined } : n)),
      };
    },
    []
  );

  // Update refs when positions/dimensions change
  useEffect(() => {
    positionsRef.current = positions;
  }, [positions]);

  useEffect(() => {
    nodeDimensionsRef.current = nodeDimensions;
  }, [nodeDimensions]);

  // Drag & Drop controller with live document manipulation
  const dragAndDrop = useMindmapDragAndDrop({
    doc: activeMindmap,
    positions: positionsRef.current,
    nodeDimensions: nodeDimensionsRef.current,
    onMove: useCallback(
      (draggedId: string, targetParentId: string | null, targetIndex: number) => {
        // On drop: commit the move to history
        // The ghost is already in the doc at the right position
        skipPreviewRef.current = true;
        queuedPreviewRef.current = null;
        previewInProgressRef.current = false;
        const baseline = baselineDocRef.current ?? null;
        const base = baseline ?? docRef.current;
        const historySnapshot = baseline && !baseline.nodes.some((node) => node.isGhost) ? baseline : undefined;
        const finalDoc = buildMovedDoc(base, draggedId, targetParentId, targetIndex, false);
        baselineDocRef.current = null;
        previewDocRef.current = null;
        lastSlotKeyRef.current = null;
        setDocWithHistory(() => finalDoc, {
          historySnapshot,
          skip: historySnapshot ? historySnapshot === finalDoc : false,
        });
      },
      [setDocWithHistory, buildMovedDoc]
    ),
    getViewport: useCallback(() => reactFlow.getViewport?.() ?? { x: 0, y: 0, zoom: 1 }, [reactFlow]),
    getPaneElement: useCallback(() => canvasRef.current?.querySelector('.react-flow__pane') as HTMLElement | null, []),
    panViewportBy: (dx: number, dy: number) => {
      try {
        const panByFromStore = storeApi.getState().panBy as any;
        if (typeof panByFromStore === 'function') panByFromStore({ x: dx, y: dy });
      } catch {}
    },
    enabled: !editingId,
  });

  const lastSlotKeyRef = useRef<string | null>(null);

  const applyPreview = useCallback(
    (draggedId: string, targetParentId: string | null, targetIndex: number) => {
      const base = baselineDocRef.current ?? docRef.current;
      if (!base) return;
      const preview = buildMovedDoc(base, draggedId, targetParentId, targetIndex, true);
      previewDocRef.current = preview;
      if (docRef.current !== preview) {
        previewInProgressRef.current = true;
        setDoc(() => preview);
      }
    },
    [buildMovedDoc, setDoc]
  );

  useEffect(() => {
    if (!dragAndDrop.isDragging) {
      queuedPreviewRef.current = null;
      previewInProgressRef.current = false;
      if (baselineDocRef.current) {
        const baseline = baselineDocRef.current;
        baselineDocRef.current = null;
        previewDocRef.current = null;
        lastSlotKeyRef.current = null;
        if (baseline && docRef.current !== baseline) {
          setDoc(() => baseline);
        }
      } else {
        previewDocRef.current = null;
        lastSlotKeyRef.current = null;
      }
      skipPreviewRef.current = false;
      return;
    }

    const dragState = dragAndDrop.dragState;
    const draggedId = dragState?.draggedId;
    if (!draggedId) return;

    if (skipPreviewRef.current) {
      queuedPreviewRef.current = null;
      previewInProgressRef.current = false;
      return;
    }

    if (!baselineDocRef.current) {
      baselineDocRef.current = docRef.current;
    }

    const dropTarget = dragState?.dropTarget;
    if (!dropTarget || dropTarget.type !== 'sibling-slot') {
      queuedPreviewRef.current = null;
      previewInProgressRef.current = false;
      if (previewDocRef.current && baselineDocRef.current) {
        const baseline = baselineDocRef.current;
        previewDocRef.current = null;
        lastSlotKeyRef.current = null;
        if (baseline && docRef.current !== baseline) {
          setDoc(() => baseline);
        }
      }
      return;
    }

    const targetParentId = dropTarget.parentId ?? null;
    const targetIndex = dropTarget.index;
    const slotKey = `${targetParentId ?? 'root'}:${targetIndex}`;
    if (slotKey === lastSlotKeyRef.current) return;
    lastSlotKeyRef.current = slotKey;

    if (previewInProgressRef.current) {
      queuedPreviewRef.current = { draggedId, parentId: targetParentId, index: targetIndex };
      return;
    }

    applyPreview(draggedId, targetParentId, targetIndex);
  }, [dragAndDrop.isDragging, dragAndDrop.dragState, applyPreview]);

  useEffect(() => {
    if (!dragAndDrop.isDragging) return;
    if (!previewInProgressRef.current) return;
    previewInProgressRef.current = false;
    const queued = queuedPreviewRef.current;
    if (queued) {
      queuedPreviewRef.current = null;
      applyPreview(queued.draggedId, queued.parentId, queued.index);
    }
  }, [doc, dragAndDrop.isDragging, applyPreview]);

  // Ensure pan guard clears after dnd session ends
  useEffect(() => {
    if (!dragAndDrop.isDragging) {
      setPanGuardActive(false);
    }
  }, [dragAndDrop.isDragging]);

  const viewportPrimedRef = useRef(false);
  useEffect(() => {
    viewportPrimedRef.current = false;
  }, [activeMindmap.id]);

  useEffect(() => {
    if (viewportPrimedRef.current) return;
    const nodes = reactFlow.getNodes?.();
    if (!nodes || nodes.length === 0) return;
    const viewport = reactFlow.getViewport?.();
    if (!viewport || typeof reactFlow.setViewport !== 'function') return;
    viewportPrimedRef.current = true;
    try {
      reactFlow.setViewport(viewport, { duration: 0 });
      requestAnimationFrame(() => {
        try {
          const vp2 = reactFlow.getViewport?.();
          if (vp2 && typeof reactFlow.setViewport === 'function') {
            reactFlow.setViewport(vp2, { duration: 0 });
          }
        } catch {}
      });
    } catch {
      viewportPrimedRef.current = false;
    }
  }, [reactFlow, baseNodes, activeMindmap.id]);

  const wasDraggingRef = useRef(false);
  useEffect(() => {
    if (wasDraggingRef.current && !dragAndDrop.isDragging && !editingId) {
      requestAnimationFrame(() => {
        try {
          canvasRef.current?.focus();
        } catch {}
      });
    }
    wasDraggingRef.current = dragAndDrop.isDragging;
  }, [dragAndDrop.isDragging, editingId]);

  // Clipboard controller
  const {
    copySubtree,
    duplicateNodeOnly,
    duplicateSubtree,
    pasteStyleTo,
    canPaste,
    version: clipboardVersion,
  } = useMindmapClipboard({
    doc,
    setDocWithHistory,
    genId,
    handleStyleCommit,
    selectedRawIdsRef,
    editingId,
  });

  useEffect(() => {
    selectedRawIdsRef.current = selectedRawIds;
  }, [selectedRawIds]);

  // Pan mode: keep default vertical wheel for zoom (React Flow),
  // but support horizontal wheel (or Shift+wheel) to pan left/right.
  useEffect(() => {
    if (uiState.mode !== 'pan') return;
    const pane = canvasRef.current?.querySelector('.react-flow__pane') as HTMLElement | null;
    const panByFromStore = storeApi.getState().panBy;
    if (!pane || typeof panByFromStore !== 'function') return;

    const handleWheel = (event: WheelEvent) => {
      // Let RF handle pinch/ctrlKey and normal vertical zoom
      if (event.ctrlKey) return;
      const normalize = event.deltaMode === 1 ? 20 : 1;
      const deltaX = event.deltaX * normalize;
      const deltaY = event.deltaY * normalize;
      const shiftHoriz = event.shiftKey && Math.abs(deltaY) > Math.abs(deltaX);
      const isHorizontal = shiftHoriz || Math.abs(deltaX) > Math.abs(deltaY);
      const panDelta = shiftHoriz ? deltaY : deltaX;
      if (isHorizontal && Math.abs(panDelta) >= 0.5) {
        event.preventDefault();
        event.stopPropagation();
        panByFromStore({ x: -panDelta, y: 0 });
      }
    };

    pane.addEventListener('wheel', handleWheel, { passive: false });
    return () => pane.removeEventListener('wheel', handleWheel);
  }, [storeApi, uiState.mode]);

  useEffect(() => {
    if (uiState.mode !== 'select') return;
    const pane = canvasRef.current?.querySelector('.react-flow__pane') as HTMLElement | null;
    const panByFromStore = storeApi.getState().panBy;
    const minZoom = typeof storeApi.getState().minZoom === 'number' ? storeApi.getState().minZoom : 0.1;
    const maxZoom = typeof storeApi.getState().maxZoom === 'number' ? storeApi.getState().maxZoom : 2;
    if (!pane || typeof panByFromStore !== 'function') return;

    const isMac = typeof navigator !== 'undefined' && /mac|ipad|iphone|ipod/i.test(navigator.platform ?? '');

    const handleWheel = (event: WheelEvent) => {
      const normalize = event.deltaMode === 1 ? 20 : 1;
      let deltaX = event.deltaX * normalize;
      let deltaY = event.deltaY * normalize;

      if (event.ctrlKey || event.metaKey) {
        const viewport = reactFlow.getViewport?.();
        if (!viewport) return;
        const nextZoom = Math.min(maxZoom, Math.max(minZoom, viewport.zoom * Math.pow(2, -deltaY * 0.002)));
        if (nextZoom === viewport.zoom) return;
        const rect = pane.getBoundingClientRect();
        const screenX = event.clientX - rect.left;
        const screenY = event.clientY - rect.top;
        const flowX = (screenX - viewport.x) / viewport.zoom;
        const flowY = (screenY - viewport.y) / viewport.zoom;
        event.preventDefault();
        event.stopPropagation();
        wheelLogger.debug('wheel zoom (select mode): current=%d next=%d', viewport.zoom, nextZoom);
        reactFlow.setViewport?.(
          {
            x: screenX - flowX * nextZoom,
            y: screenY - flowY * nextZoom,
            zoom: nextZoom,
          },
          { duration: 0 }
        );
        return;
      }

      if (!isMac && event.shiftKey) {
        deltaX = deltaY;
        deltaY = 0;
      }

      event.preventDefault();
      event.stopPropagation();
      panByFromStore({ x: -deltaX, y: -deltaY });
      wheelLogger.debug('wheel pan (select mode): deltaX=%d deltaY=%d', deltaX, deltaY);
    };

    pane.addEventListener('wheel', handleWheel, { passive: false });
    return () => pane.removeEventListener('wheel', handleWheel);
  }, [wheelLogger, reactFlow, storeApi, uiState.mode]);

  useEffect(() => {
    renameLogger.debug(
      'editingId update: editing=%s pending=%s cancelRef=%s',
      editingId ?? 'null',
      pendingEditText ?? 'null',
      cancelEditRef.current
    );
  }, [editingId, pendingEditText, renameLogger]);

  // (reverted) No transition warm-up gating; rely on global CSS always-on transitions.

  useEffect(() => {
    if (editingId) return;
    if (editingInputRef.current || editingInputIdRef.current) {
      renameLogger.debug('editing ended — clearing active input ref');
      editingInputRef.current = null;
      editingInputIdRef.current = null;
    }
  }, [editingId, renameLogger]);

  // Aggressively enforce focus/selection for a short window after edit starts (handles RF re-renders)
  useEffect(() => {
    if (!editingId) return;
    let attempts = 0;
    let rafId = 0 as unknown as number;
    const run = () => {
      if (!editingId) return; // edit ended
      const el = editingInputRef.current;
      if (el && document.activeElement !== el) {
        try {
          el.focus();
          if (editingSelectionModeRef.current === 'select-all') {
            el.select();
          } else {
            const end = el.value.length;
            el.setSelectionRange(end, end);
          }
          renameLogger.debug(
            'focus-enforce tick=%d id=%s sel=[%d,%d] mode=%s',
            attempts,
            editingInputIdRef.current ?? 'n/a',
            el.selectionStart ?? -1,
            el.selectionEnd ?? -1,
            editingSelectionModeRef.current
          );
        } catch {}
      }
      attempts += 1;
      if (attempts < 6 && editingId) {
        rafId = window.requestAnimationFrame(run);
      }
    };
    rafId = window.requestAnimationFrame(run);
    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
    };
  }, [editingId, renameLogger]);

  const isDebouncedKey = useCallback((code: string, delay = 150) => {
    const now = Date.now();
    const last = lastKeyRef.current;
    if (last.code === code && now - last.ts < delay) {
      return true;
    }
    lastKeyRef.current = { code, ts: now };
    return false;
  }, []);

  // Persisted load
  // Auto-insert audio transcript only when a node is actively in edit mode
  useEffect(() => {
    const unsubscribe = AudioTranscriptionEventBus.subscribe((detail) => {
      const text = String(detail.text || '').trim();
      if (!text) return;

      // Only insert when editing an input
      const input = editingInputRef.current;
      if (editingId && input) {
        try {
          // Temporarily suppress blur-commit in case clipboard fallback steals focus
          cancelEditRef.current = true;
          window.setTimeout(() => {
            cancelEditRef.current = false;
          }, 350);

          const start = input.selectionStart ?? input.value.length;
          const end = input.selectionEnd ?? start;
          const before = input.value.slice(0, start);
          const after = input.value.slice(end);
          const nextValue = before + text + after;
          input.value = nextValue;
          const caret = (before + text).length;
          const ensureCaretVisible = () => {
            const el = editingInputRef.current;
            if (!el) return;
            try {
              el.focus();
              el.setSelectionRange(caret, caret);
              // Make sure caret is scrolled into view for long text
              el.scrollLeft = el.scrollWidth;
            } catch {}
          };

          // Apply immediately and reinforce over a few frames to survive reflows
          ensureCaretVisible();
          let attempts = 0;
          const tick = () => {
            attempts += 1;
            ensureCaretVisible();
            if (attempts < 6) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
          return;
        } catch {
          // If insertion fails for any reason, ignore (no fallback)
        }
      }
      // Not in edit mode: do nothing
    });
    return () => {
      try {
        unsubscribe?.();
      } catch {}
    };
  }, [editingId, setDocWithHistory]);

  // Debounced save
  useEffect(() => {
    saverRef.current.save(doc);
  }, [doc]);

  const rfIndexByRawIdRef = useRef<Map<string, number>>(new Map());

  const updateRFSelectionOptimized = useCallback(
    (ids: string[]) => {
      const nextSet = new Set(ids);
      const prevSet = new Set(selectedRawIdsRef.current);
      const changed: string[] = [];
      for (const id of prevSet) if (!nextSet.has(id)) changed.push(id);
      for (const id of nextSet) if (!prevSet.has(id)) changed.push(id);
      if (changed.length === 0) return;

      reactFlow?.setNodes?.((prev: any[]) => {
        const indexMap = rfIndexByRawIdRef.current;
        if (indexMap.size !== prev.length) {
          indexMap.clear();
          for (let i = 0; i < prev.length; i += 1) {
            const raw = toRawId(prev[i].id as string);
            indexMap.set(raw, i);
          }
        }

        let nextArr = prev;
        let updated = false;
        for (const rawId of changed) {
          const idx = indexMap.get(rawId);
          if (idx == null) continue;
          const node = nextArr[idx];
          if (!node) continue;
          const shouldSelect = nextSet.has(rawId);
          const isSelected = !!(node.selected ?? false);
          if (isSelected !== shouldSelect) {
            if (nextArr === prev) nextArr = prev.slice();
            nextArr[idx] = { ...node, selected: shouldSelect };
            updated = true;
          }
        }
        return updated ? nextArr : prev;
      });
    },
    [reactFlow]
  );

  const queueSelection = useCallback(
    (ids: string[]) => {
      pendingSelectionRef.current = ids;
      selectedRawIdsRef.current = ids;
      if (typeof window !== 'undefined' && 'requestAnimationFrame' in window) {
        window.requestAnimationFrame(() => setSelectedRawIds(ids));
      } else {
        setTimeout(() => setSelectedRawIds(ids), 0);
      }
      updateRFSelectionOptimized(ids);
    },
    [updateRFSelectionOptimized]
  );

  // Reset viewport initialization flag when mindmap changes
  useEffect(() => {
    didInitialFitRef.current = false;
  }, [activeMindmap.id]);

  // One-time: restore saved viewport if available, else center on root (slightly increased zoom)
  useEffect(() => {
    if (didInitialFitRef.current) return;
    if (!baseNodes.length) return;
    const pane = canvasRef.current?.querySelector('.react-flow__pane') as HTMLElement | null;
    if (!pane) return;

    const rfRootId = `${activeMindmap.id}::${activeMindmap.rootId}`;

    // Try restore saved viewport per mindmap
    try {
      const saved = loadMindmapViewport(activeMindmap.id);
      if (saved) {
        reactFlow.setViewport?.({ x: saved.x, y: saved.y, zoom: saved.zoom }, { duration: 0 });
        viewportZoomRef.current = saved.zoom;
        didInitialFitRef.current = true;
        return;
      }
    } catch {}

    // Compute a slightly larger starting zoom (about +10%), clamped to store min/max
    const computeTargetZoom = () => {
      const viewport = reactFlow.getViewport?.();
      const baseZoom = viewport?.zoom ?? 1;
      const store = storeApi.getState?.();
      const minZoom = typeof store?.minZoom === 'number' ? store!.minZoom : 0.1;
      const maxZoom = typeof store?.maxZoom === 'number' ? store!.maxZoom : 2;
      const desired = baseZoom * 1.15; // slight zoom in
      return Math.max(minZoom, Math.min(maxZoom, desired));
    };

    const centerOnRoot = () => {
      try {
        const zoom = computeTargetZoom();
        const rect = pane.getBoundingClientRect();
        const screenX = rect.width / 2;
        const screenY = rect.height / 2;

        const rootPos = positions[activeMindmap.rootId] ?? { x: 0, y: 0 };
        let cx = rootPos.x;
        let cy = rootPos.y;

        const rfNodes: any[] = (reactFlow.getNodes?.() as any[]) || [];
        const rfRoot: any = rfNodes.find((n) => n.id === rfRootId);
        if (rfRoot && typeof rfRoot.width === 'number' && typeof rfRoot.height === 'number') {
          cx = rootPos.x + (rfRoot.width || 0) / 2;
          cy = rootPos.y + (rfRoot.height || 0) / 2;
        }

        const x = screenX - cx * zoom;
        const y = screenY - cy * zoom;
        reactFlow.setViewport?.({ x, y, zoom }, { duration: 0 });
      } catch {}
    };

    // Apply once now and mark as done to avoid update loops
    centerOnRoot();
    didInitialFitRef.current = true;
    // Optionally recenter once dimensions are known within a few frames
    let tries = 0;
    let raf = 0 as unknown as number;
    const tick = () => {
      tries += 1;
      const rfNodes: any[] = (reactFlow.getNodes?.() as any[]) || [];
      const rfRoot: any = rfNodes.find((n) => n.id === rfRootId);
      if (rfRoot && typeof rfRoot.width === 'number' && rfRoot.width > 0) {
        centerOnRoot();
        return;
      }
      if (tries < 6) {
        raf = window.requestAnimationFrame(tick);
      }
    };
    raf = window.requestAnimationFrame(tick);
    return () => {
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [baseNodes, positions, activeMindmap, reactFlow, storeApi]);

  const rfNodes = useMemo(() => {
    const selectedSet = new Set(selectedRawIds);
    const effectiveDirection = layoutPreview ?? activeMindmap.layoutDirection ?? 'right';
    const nodes = baseNodes.map((node) => {
      const baseData = node.data as MindmapNodeData;
      const rawId = baseData.id;
      const isRoot = baseData.isRoot ?? false;

      const mergedData: RuntimeMindmapNodeData = {
        ...baseData,
        isEditing: rawId === editingId,
        initialEditValue: rawId === editingId && pendingEditText != null ? pendingEditText : undefined,
        shouldSkipCommit: () => cancelEditRef.current,
        dndEnabled: dragAndDrop.isDragEnabled && !baseData.isGhost,
        onToggleCollapse: (id: string, next: boolean) => {
          setDocWithHistory((prev) => toggleNodeCollapse(prev, id, next));
          // Save collapsed set to view record immediately (without waiting for autosave)
          try {
            const prevSet = new Set(docRef.current.nodes.filter((n) => n.collapsed).map((n) => n.id));
            if (next) prevSet.add(id);
            else prevSet.delete(id);
            saveMindmapCollapsed(activeMindmap.id, Array.from(prevSet));
          } catch {}
          // Ensure selection remains on the toggled node, and hide hover toolbar
          queueSelection([id]);
          clearHoveredNode();
          hideToolbar();
        },
        // Suppress canvas pan while user initiates a node drag (pre-drag window)
        onDragCandidatePointerDown: () => {
          setPanGuardActive(true);
        },
        onDragCandidatePointerUp: () => {
          setPanGuardActive(false);
        },
        onContextMenuOpen: () => {
          queueSelection([rawId]);
          hideToolbar();
        },
        onCreateSiblingAfter: () => {
          const result = createSiblingAfter(doc, rawId);
          setDocWithHistory(() => result.mindmap);
          queueSelection([result.selectedId ?? rawId]);
        },
        onCreateSiblingBefore: () => {
          const result = createSiblingBefore(doc, rawId);
          setDocWithHistory(() => result.mindmap);
          queueSelection([result.selectedId ?? rawId]);
        },
        onCreateChildFirst: () => {
          const result = createChildFirst(doc, rawId);
          setDocWithHistory(() => result.mindmap);
          queueSelection([result.selectedId]);
        },
        onCreateChildLast: () => {
          const result = createChildLast(doc, rawId);
          setDocWithHistory(() => result.mindmap);
          queueSelection([result.selectedId]);
        },
        onChangeBgColor: (color) => {
          handleStyleCommit(rawId, { bgColor: color });
        },
        onChangeBorderColor: (color) => {
          handleStyleCommit(rawId, { borderColor: color });
        },
        onChangeShape: (nextShape) => {
          handleStyleCommit(rawId, { shape: nextShape });
        },
        onChangeDirection: (directionOption) => {
          if (isRoot) {
            handleDirectionCommit(directionOption);
          }
        },
        onCopyNode: () => copySubtree(rawId),
        onPasteNode: () => duplicateNodeOnly(rawId),
        onPasteSubtree: () => duplicateSubtree(rawId),
        onPasteStyle: () => pasteStyleTo(rawId),
        canPaste,
        // Embedded toolbar callbacks for this specific node
        onNodeStylePreview: (patch) => handleStylePreview(rawId, patch as any),
        onNodeStyleCommit: (patch) => handleStyleCommit(rawId, patch as any),
        onNodeDirectionPreview: handleDirectionPreview,
        onNodeDirectionCommit: handleDirectionCommit,
        onNodeDirectionReset: handleDirectionReset,
        selectedId: rawId,
        selectedStyle: {
          textColor: baseData.textColor,
          bgColor: baseData.bgColor,
          borderColor: baseData.borderColor,
          shape: (baseData.shape as any) ?? 'rounded',
        },
        disabledToolbar: !!editingId,
        isDraggingToolbar: false,
        viewportZoom: viewportZoomRef.current,
        onCommitRename: (id: string, value: string) => {
          renameLogger.debug('commit request: id=%s value=%s', id, value);
          setDocWithHistory((prev) => renameNode(prev, id, value));
          cancelEditRef.current = false;
          setEditingId(null);
          setPendingEditText(null);
          renameLogger.debug('commit applied: id=%s', id);
          // Ensure keyboard handlers work immediately after commit
          setTimeout(() => {
            canvasRef.current?.focus();
          }, 0);
        },
        onCancelRename: () => {
          renameLogger.debug('cancel edit: id=%s', rawId);
          cancelEditRef.current = true;
          setEditingId(null);
          setPendingEditText(null);
          renameLogger.debug('cancel applied: id=%s', rawId);
          setTimeout(() => {
            canvasRef.current?.focus();
          }, 0);
        },
        onHoverChange: (hovered: boolean) => handleNodeHoverChange(rawId, hovered),
        logger: renameLogger,
        registerEditRef: (element) => {
          if (element && rawId === editingId) {
            editingInputRef.current = element;
            editingInputIdRef.current = rawId;
            renameLogger.debug('edit ref registered: id=%s value=%s', rawId, element.value);
            const focusTask = () => {
              if (editingInputRef.current !== element) return;
              if (document.activeElement !== element) {
                element.focus();
              }
              try {
                if (editingSelectionModeRef.current === 'select-all') {
                  element.select();
                } else {
                  const end = element.value.length;
                  element.setSelectionRange(end, end);
                }
              } catch {
                // ignore if selection fails
              }
              renameLogger.debug(
                'edit ref focus enforced: id=%s value=%s sel=[%d,%d] mode=%s',
                rawId,
                element.value,
                element.selectionStart ?? -1,
                element.selectionEnd ?? -1,
                editingSelectionModeRef.current
              );
            };
            if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
              window.requestAnimationFrame(focusTask);
            } else {
              setTimeout(focusTask, 0);
            }
            return;
          }

          if (!element && editingInputIdRef.current === rawId) {
            renameLogger.debug('edit ref cleared: id=%s', rawId);
            editingInputRef.current = null;
            editingInputIdRef.current = null;
          }
        },
        selectionMode: rawId === editingId ? editingSelectionModeRef.current : undefined,
      };
      mergedData.layoutDirection = effectiveDirection;
      return {
        ...node,
        className: exitingNodes.has(rawId) ? 'mindmap-node-exiting' : undefined,
        draggable: false,
        selectable: !isRoot,
        selected: selectedSet.has(rawId) || rawId === editingId,
        // (reverted) No per-node inline style for transitions/will-change here
        data: mergedData,
      };
    });
    return nodes;
  }, [
    baseNodes,
    activeMindmap,
    editingId,
    handleNodeHoverChange,
    layoutPreview,
    pendingEditText,
    selectedRawIds,
    renameLogger,
    editingInputRef,
    editingInputIdRef,
    hideToolbar,
    clearHoveredNode,
    handleStylePreview,
    handleStyleCommit,
    handleDirectionPreview,
    handleDirectionCommit,
    handleDirectionReset,
    queueSelection,
    createChildFirst,
    createChildLast,
    createSiblingAfter,
    createSiblingBefore,
    doc,
    previewDoc,
    setDocWithHistory,
    reactFlow,
    exitingNodes,
    clipboardVersion,
    dragAndDrop.isDragEnabled,
  ]);

  // Ghost node is now a real node in the document, so no special placeholder rendering needed

  // Ghost node is now a real node, so edges are handled automatically by buildReactFlowEdges
  const rfEdges = useMemo(() => {
    return buildReactFlowEdges(activeMindmap, activeMindmap.id);
  }, [activeMindmap]);

  // Selection helpers --------------------------------------------------------
  const applySelection = useCallback(
    (rawIds: string[]) => {
      if (areArraysEqual(selectedRawIdsRef.current, rawIds)) return;
      selectedRawIdsRef.current = rawIds;
      setSelectedRawIds(rawIds);
      updateRFSelectionOptimized(rawIds);
    },
    [updateRFSelectionOptimized]
  );

  useEffect(() => {
    if (pendingSelectionRef.current) {
      applySelection(pendingSelectionRef.current);
      pendingSelectionRef.current = null;
    }
  }, [baseNodes, applySelection]);

  useEffect(() => {
    try {
      const current = reactFlow?.getViewport?.();
      if (current && typeof current.x === 'number') {
        viewportZoomRef.current = current.zoom;
      } else {
        viewportZoomRef.current = 1;
      }
    } catch {
      viewportZoomRef.current = 1;
    }
  }, [reactFlow]);

  const primarySelected = selectedRawIds[selectedRawIds.length - 1] ?? null;

  const uiFlags = useMemo(() => getInteractionFlags(uiState.mode), [uiState.mode]);

  // Persistence / export+import helpers -------------------------------------

  const focusCanvas = useCallback(() => {
    canvasRef.current?.focus();
  }, []);

  const onAfterImport = useCallback(
    (imported: Mindmap) => {
      // Replace the current document (with history) so UI re-renders to the selected map
      resetHistory(imported);
      resetLayoutState(imported);
      // Allow initial fit for the new map
      try {
        (didInitialFitRef as any).current = false;
      } catch {}
      pendingSelectionRef.current = [imported.rootId];
      selectedRawIdsRef.current = [imported.rootId];
      setSelectedRawIds([imported.rootId]);
      setUiState(getInitialUIState());
      hideToolbar();
      clearHoveredNode();
      focusCanvas();
      saverRef.current.cancel();
      saverRef.current.save(imported);
    },
    [clearHoveredNode, focusCanvas, hideToolbar, resetHistory, resetLayoutState, setSelectedRawIds, setUiState]
  );

  const { exportAsMindmapText, exportAsList, exportAsPng, importFromFile, downloadSnapshot, triggerImportDialog } =
    useMindmapIO({
      doc,
      resetHistory,
      logger,
      hideToolbar,
      reactFlow,
      canvasRef,
      fileInputRef,
      onAfterImport,
    });

  // Clipboard keyboard handlers moved into useMindmapClipboard

  const { isLassoActive, lassoPath, paneOrigin, allowLasso, consumePaneClickSuppression } = useMindmapLasso({
    canvasRef,
    mode: uiState.mode,
    isEditing: !!editingId,
    hasHoveredNode: hoveredNodeId != null,
  });

  const handleNodeClick = useCallback(
    (event: React.MouseEvent, node: { data: MindmapNodeData }) => {
      if (!editingId) {
        focusCanvas();
      }
      const rawId = node.data.id;
      logger.debug('node click %s meta=%s ctrl=%s', rawId, event.metaKey, event.ctrlKey);
      if (event.metaKey || event.ctrlKey) {
        event.preventDefault();
        const current = selectedRawIdsRef.current;
        const next = new Set(current);
        if (next.has(rawId)) {
          next.delete(rawId);
        } else {
          next.add(rawId);
        }
        applySelection(Array.from(next));
        return;
      }

      applySelection([rawId]);
    },
    [applySelection, focusCanvas, logger, editingId]
  );

  const handleNodeDoubleClick = useCallback(
    (event: React.MouseEvent, node: { data: MindmapNodeData }) => {
      event.preventDefault();
      event.stopPropagation();
      const rawId = node.data.id;
      renameLogger.debug('node doubleclick — start edit: %s', rawId);
      cancelEditRef.current = false;
      editingSelectionModeRef.current = 'caret-end';
      setEditingId(rawId);
      setPendingEditText(null);
      // ensure selection stays on the edited node
      applySelection([rawId]);
    },
    [applySelection, renameLogger]
  );

  const handleSelectionChange = useCallback(
    ({ nodes }: OnSelectionChangeParams) => {
      const rawIds = nodes.map((node) => toRawId(node.id));
      logger.debug('ReactFlow onSelectionChange %o', rawIds);
      if (areArraysEqual(selectedRawIdsRef.current, rawIds)) return;

      if (viewportInteractingRef.current) {
        perfLogger.warn('🟡 onSelectionChange during pan/zoom', {
          from: selectedRawIdsRef.current,
          to: rawIds,
        });
      }

      selectedRawIdsRef.current = rawIds;
      setSelectedRawIds(rawIds);
    },
    [logger, perfLogger]
  );

  // Ensure viewport interaction flag never gets stuck across mode changes
  useEffect(() => {
    viewportInteractingRef.current = false;
  }, [uiState.mode]);

  const handleViewportInteractionStart = useCallback(
    (event?: any, vp?: { x: number; y: number; zoom: number }) => {
      if (viewportInteractingRef.current) return; // Already interacting, ignore

      viewportInteractingRef.current = true;
      viewportInteractionStartTimeRef.current = performance.now();

      // Update zoom ref (doesn't trigger re-render)
      if (vp?.zoom) viewportZoomRef.current = vp.zoom;

      // Disable CSS transitions during pan/zoom for better performance
      if (canvasRef.current) {
        canvasRef.current.setAttribute('data-viewport-interacting', 'true');
      }

      perfLogger.debug('Pan/Zoom START');

      // If the interaction starts from the background pane, treat it like an outside click:
      // clear selection and hide the toolbar so dragging pans doesn't keep toolbars visible.
      try {
        const target = event?.target as HTMLElement | null;
        if (target && target.closest?.('.react-flow__pane')) {
          applySelection([]);
          clearHoveredNode();
          hideToolbar();
        }
      } catch {}

      if (!DISABLE_TOOLBAR_PAN_GATING) {
        clearHoveredNode();
        hideToolbar();
      }
    },
    [applySelection, clearHoveredNode, hideToolbar, perfLogger]
  );

  const handleViewportInteractionEnd = useCallback(
    (event?: any, vp?: { x: number; y: number; zoom: number }) => {
      if (!viewportInteractingRef.current) return; // Not interacting, ignore

      viewportInteractingRef.current = false;

      // Update zoom ref (doesn't trigger re-render)
      if (vp?.zoom) viewportZoomRef.current = vp.zoom;

      // Re-enable CSS transitions after pan/zoom ends
      if (canvasRef.current) {
        canvasRef.current.removeAttribute('data-viewport-interacting');
      }

      const duration = performance.now() - viewportInteractionStartTimeRef.current;
      perfLogger.info('Pan/Zoom END', { durationMs: duration.toFixed(2) });

      try {
        const current = vp ?? reactFlow.getViewport?.();
        if (
          current &&
          typeof current.x === 'number' &&
          typeof current.y === 'number' &&
          typeof current.zoom === 'number'
        ) {
          saveMindmapViewport(activeMindmap.id, { x: current.x, y: current.y, zoom: current.zoom });
        }
      } catch {}
    },
    [perfLogger, reactFlow, activeMindmap.id]
  );

  const handleViewportInteractionMove = useCallback((event?: any, vp?: { x: number; y: number; zoom: number }) => {
    viewportInteractingRef.current = true;
    // Update zoom ref during pan/zoom (doesn't trigger re-render)
    if (vp?.zoom) viewportZoomRef.current = vp.zoom;
    // Don't update state during interaction - only update ref
  }, []);

  // Keyboard handlers --------------------------------------------------------
  const handleCreateChild = useCallback(
    (first: boolean) => {
      const base = primarySelected;
      if (!base) return;
      const op = first ? createChildFirst : createChildLast;
      const res = op(doc, base);
      setDocWithHistory(() => res.mindmap);
      queueSelection([res.selectedId]);
    },
    [doc, primarySelected]
  );

  const handleCreateSibling = useCallback(
    (before: boolean) => {
      const base = primarySelected;
      if (!base) return;
      const op = before ? createSiblingBefore : createSiblingAfter;
      const res = op(doc, base);
      setDocWithHistory(() => res.mindmap);
      queueSelection([res.selectedId]);
    },
    [doc, primarySelected]
  );

  const handleDeleteSelection = useCallback(() => {
    const ids = selectedRawIdsRef.current;
    if (!ids.length) return;
    const map = new Map(doc.nodes.map((n) => [n.id, n]));
    const depth = (id: string) => {
      let d = 0;
      let cur = id;
      while (map.get(cur)?.parentId) {
        d += 1;
        cur = map.get(cur)!.parentId!;
      }
      return d;
    };
    const ordered = [...ids].sort((a, b) => depth(b) - depth(a));
    let nextDoc = doc;
    let fallback: string | null = null;
    for (const id of ordered) {
      const result = deleteSubtree(nextDoc, id);
      nextDoc = result.mindmap;
      fallback = result.selectedId ?? fallback;
    }
    setDocWithHistory(() => nextDoc);
    queueSelection(fallback ? [fallback] : []);
  }, [doc, queueSelection]);

  const handleMoveSelection = useCallback(
    (direction: 'left' | 'right' | 'up' | 'down') => {
      const base = primarySelected;
      if (!base) return;
      let target: string | null = base;
      if (direction === 'left') {
        target = getParentIdOf(doc, base) ?? base;
      }
      if (direction === 'right') {
        const children = getSiblingsSortedByPositionUtil(doc, positions, base);
        target = children.length ? children[0] : base;
      }
      if (direction === 'up' || direction === 'down') {
        const parentId = getParentIdOf(doc, base);
        const sibs = getSiblingsSortedByPositionUtil(doc, positions, parentId);
        const idx = sibs.indexOf(base);
        if (idx >= 0) {
          if (direction === 'up' && idx > 0) target = sibs[idx - 1];
          if (direction === 'down' && idx < sibs.length - 1) target = sibs[idx + 1];
        }
      }
      queueSelection(target ? [target] : []);
    },
    [doc, positions, primarySelected, queueSelection]
  );

  const { handleNodeKeyDown } = useKeyboardMindmapControls({
    editingId,
    renameLogger,
    editingInputRef,
    cancelEditRef,
    editingSelectionModeRef,
    setPendingEditText,
    setEditingId,
    primarySelected,
    isDebouncedKey,
    handleCreateSibling,
    handleCreateChild,
    handleDeleteSelection,
    handleMoveSelection,
    applySelection,
    undo,
    redo,
    canvasRef,
    focusCanvas,
    selectedRawIdsRef,
  });

  // Mode toggle (double-click on background)
  useEffect(() => {
    const handler = (event: MouseEvent) => {
      const root = canvasRef.current;
      if (!root) return;
      const target = event.target as HTMLElement | null;
      const inside = !!(target && root.contains(target));
      const isNode = !!target?.closest('.react-flow__node');
      const isControls = !!target?.closest('.react-flow__controls');
      const isMiniMap = !!target?.closest('.react-flow__minimap');
      if (inside && !isNode && !isControls && !isMiniMap) {
        setUiState((prev) => toggleMode(prev));
      }
    };
    window.addEventListener('dblclick', handler, true);
    return () => window.removeEventListener('dblclick', handler, true);
  }, []);

  // Global Tab handler moved into useKeyboardMindmapControls

  const handlePaneClick = () => {
    if (consumePaneClickSuppression()) return;
    applySelection([]);
    clearHoveredNode();
    focusCanvas();
  };

  return (
    <div className='flex h-full min-h-[600px] w-full flex-col'>
      <input
        ref={fileInputRef}
        type='file'
        accept='.mmp,.mmp.txt,.mmp.text,.mmp.md,.mmp.mdx,.txt,.md,.mdx,application/json,text/plain'
        className='hidden'
        onChange={importFromFile}
      />
      <MindmapCanvasContextMenu
        onOpenChange={(open) => {
          if (open) hideToolbar();
        }}
        onExportAsPng={exportAsPng}
        onExportAsMindmapText={exportAsMindmapText}
        onExportAsList={exportAsList}
        onImportMindmap={triggerImportDialog}
        onDownloadMindmap={downloadSnapshot}>
        <div
          data-testid='mindmap-canvas'
          data-hotkey-scope='local'
          role='main'
          ref={canvasRef}
          tabIndex={0}
          onKeyDown={handleNodeKeyDown}
          className={`relative h-full w-full flex-1 focus:outline-none focus:ring-0 ${
            uiState.mode === 'pan' ? 'cursor-grab' : 'cursor-default'
          }`}>
          {/* Floating top-left map switcher */}
          <div className='pointer-events-none absolute left-3 top-3 z-20'>
            <MindmapSwitcher
              className='pointer-events-auto'
              onSwitch={onAfterImport}
              onOpenChange={(open) => {
                if (open) {
                  // Deselect any selected nodes when opening the switcher
                  applySelection([]);
                  clearHoveredNode();
                }
              }}
            />
          </div>

          {/* Floating top-right info + save status */}
          <div className='pointer-events-none absolute right-3 top-3 z-20'>
            <div className='flex items-center gap-2'>
              <div
                className='flex items-center gap-1 text-xs font-medium select-none'
                aria-live='polite'
                aria-atomic='true'
                title={saveState === 'dirty' ? 'Unsaved changes' : 'All changes saved'}>
                {saveState === 'dirty' ? (
                  <LuClock className='h-4 w-4 text-amber-500/90 animate-pulse' aria-hidden />
                ) : (
                  <LuCheck className='h-4 w-4 text-emerald-500/90' aria-hidden />
                )}
              </div>
              <MindmapInfo className='pointer-events-auto' />
            </div>
          </div>

          <DndContext {...dragAndDrop.dndContextProps}>
            <ReactFlow
              proOptions={{ hideAttribution: true }}
              nodeTypes={nodeTypes}
              nodes={rfNodes as any}
              edges={rfEdges}
              onInit={(instance) => {
                try {
                  const vp = instance.getViewport?.();
                  if (vp) instance.setViewport?.(vp, { duration: 0 });
                  // Double-prime after a microtask just in case
                  setTimeout(() => {
                    const vp2 = instance.getViewport?.();
                    if (vp2) instance.setViewport?.(vp2, { duration: 0 });
                  }, 0);
                } catch {}
              }}
              nodesDraggable={true}
              nodesConnectable={false}
              nodesFocusable={false}
              edgesFocusable={false}
              elementsSelectable={true}
              // Prevent page scroll so wheel events reach RF for zooming
              preventScrolling={true}
              autoPanOnNodeDrag={false}
              onNodesChange={(changes) => {
                // Log changes during pan/zoom to identify culprits
                if (viewportInteractingRef.current && changes.length > 0) {
                  const changeTypes = changes.map((ch: any) => ch.type).join(',');
                  perfLogger.warn('🟡 onNodesChange during pan/zoom', {
                    types: changeTypes,
                    count: changes.length,
                  });
                }

                let changed = false;
                setNodeDimensions((prev) => {
                  const next = { ...prev };
                  for (const ch of changes as any[]) {
                    if (ch.type === 'dimensions' && ch.id && ch.dimensions) {
                      const rawId = toRawId(ch.id);
                      const w = ch.dimensions.width as number | undefined;
                      const h = ch.dimensions.height as number | undefined;
                      if (typeof w === 'number' && typeof h === 'number') {
                        const cur = next[rawId];
                        if (!cur || cur.width !== w || cur.height !== h) {
                          next[rawId] = { width: w, height: h };
                          changed = true;
                          if (viewportInteractingRef.current) {
                            perfLogger.warn('🔴 setNodeDimensions during pan/zoom', { id: rawId });
                          }
                        }
                      }
                    }
                  }
                  return changed ? next : prev;
                });
              }}
              panOnDrag={uiFlags.panOnDrag && !dragAndDrop.isDragging && !panGuardActive}
              selectionOnDrag={uiFlags.selectionOnDrag}
              panOnScroll={uiFlags.panOnScroll}
              zoomOnScroll={uiFlags.zoomOnScroll}
              multiSelectionKeyCode='Meta'
              zoomOnDoubleClick={false}
              onPaneClick={handlePaneClick}
              onMoveStart={handleViewportInteractionStart}
              onMove={handleViewportInteractionMove}
              onMoveEnd={handleViewportInteractionEnd}
              onSelectionChange={handleSelectionChange}
              onNodeClick={handleNodeClick as any}
              onNodeDoubleClick={handleNodeDoubleClick as any}
              selectionMode={SelectionMode.Partial}>
              <Background gap={24} size={1} />
              <div data-testid='mindmap-minimap' aria-label='Minimap'>
                <MiniMap
                  position='bottom-right'
                  pannable
                  zoomable
                  // Per docs, MiniMap dimensions are controlled via style, not width/height props.
                  style={{ right: 36, bottom: 0, width: 160, height: 106 }}
                />
              </div>
              <MindmapControls position='bottom-right' />
              {allowLasso && (
                <MindmapLasso
                  partial={false}
                  path={lassoPath}
                  isActive={isLassoActive}
                  origin={paneOrigin}
                  onSelectionChange={applySelection}
                />
              )}
              <DragFeedback
                dragState={dragAndDrop.dragState}
                nodes={activeMindmap.nodes}
                nodeDimensions={new Map(Object.entries(nodeDimensions))}
                nodePositions={new Map(Object.entries(positions))}
                layoutDirection={activeMindmap.layoutDirection ?? 'right'}
                viewport={reactFlow.getViewport?.() ?? { x: 0, y: 0, zoom: 1 }}
              />
            </ReactFlow>
          </DndContext>

          {/* Global style toolbar: bottom-center overlay, applies to selected node */}
          {toolbarNode &&
            !editingId &&
            (() => {
              const effective = previewDoc ?? doc;
              const node = effective.nodes.find((n) => n.id === toolbarNode.id);
              if (!node) return null;
              const selectedStyle = {
                textColor: node.textColor,
                bgColor: node.bgColor,
                borderColor: node.borderColor,
                shape: (node.shape as any) ?? 'rounded',
              } as any;
              const dir = layoutPreview ?? activeMindmap.layoutDirection ?? 'right';
              return (
                <div className='pointer-events-none absolute bottom-6 left-1/2 z-30 -translate-x-1/2'>
                  <div
                    className='pointer-events-auto'
                    style={{ transform: 'scale(1.2)', transformOrigin: 'bottom center' }}>
                    <NodeStyleMenubar
                      isRoot={toolbarNode.isRoot}
                      selected={selectedStyle}
                      selectedId={toolbarNode.id}
                      layoutDirection={dir as any}
                      disabled={!!editingId}
                      onPreview={(patch) => handleStylePreview(toolbarNode.id, patch as any)}
                      onCommit={(patch) => handleStyleCommit(toolbarNode.id, patch as any)}
                      onPreviewDirection={handleDirectionPreview}
                      onResetDirectionPreview={handleDirectionReset}
                      onChangeDirection={handleDirectionCommit}
                    />
                  </div>
                </div>
              );
            })()}
          <div
            data-testid='mindmap-mode'
            className='pointer-events-none absolute left-3 bottom-3 rounded bg-background/70 px-2 py-0.5 text-[10px] text-muted-foreground'>
            {uiState.mode}
          </div>
        </div>
      </MindmapCanvasContextMenu>
      <KeyboardShortcutsLegend />
      <style jsx global>{`
        [data-testid='mindmap-canvas'] .react-flow__nodesselection-rect {
          pointer-events: none !important;
        }
        /* Hide connector dots (handles) while keeping them functional for edge anchors */
        [data-testid='mindmap-canvas'] .react-flow__handle {
          opacity: 0 !important;
          pointer-events: none !important;
        }
        /* Enable smooth transform transitions for nodes */
        [data-testid='mindmap-canvas'] .react-flow__node {
          transition: transform 240ms cubic-bezier(0.2, 0, 0.2, 1);
          will-change: transform;
        }
        ${dragFeedbackStyles}
        /* Disable transitions during pan/zoom for smooth 60fps performance */
        [data-testid='mindmap-canvas'][data-viewport-interacting='true'] .react-flow__node {
          transition: none !important;
          will-change: transform;
        }

        /* Disable expensive backdrop blur during pan/zoom only - keeps background colors intact */
        [data-testid='mindmap-canvas'][data-viewport-interacting='true'] .react-flow__node > div {
          backdrop-filter: none !important;
        }
        .mindmap-lasso-overlay {
          pointer-events: none;
          position: absolute;
          inset: 0;
          z-index: 5;
          touch-action: none;
        }

        /* Dark mode support: apply React Flow dark mode variables when .dark ancestor exists */
        .dark .react-flow {
          --xy-controls-button-background-color-default: #2b2b2b;
          --xy-controls-button-background-color-hover-default: #3e3e3e;
          --xy-controls-button-color-default: #f8f8f8;
          --xy-controls-button-color-hover-default: #fff;
          --xy-controls-button-border-color-default: #5b5b5b;
          --xy-minimap-background-color-default: #141414;
          --xy-minimap-mask-background-color-default: rgba(60, 60, 60, 0.6);
          --xy-minimap-node-background-color-default: #2b2b2b;
          --xy-background-pattern-dots-color-default: #777;
        }
      `}</style>
    </div>
  );
}

export function MindmapCanvas({ mindmap: propMindmap }: MindmapCanvasProps) {
  const [mindmap, setMindmap] = useState<Mindmap | null>(() => {
    if (typeof window === 'undefined') {
      return propMindmap ?? null;
    }
    return propMindmap ?? null;
  });

  useEffect(() => {
    if (propMindmap) {
      setMindmap(propMindmap);
      return;
    }
    if (mindmap) return;
    try {
      const loaded = loadMindmap();
      setMindmap(loaded);
    } catch {
      // loadMindmap() should never fail because it falls back to loadActiveMindmap()
      // which calls ensureIndex() and creates a seed with proper UUID if needed.
      // If it does fail, set null to show error state rather than creating legacy ID.
      setMindmap(null);
    }
  }, [propMindmap, mindmap]);

  if (!mindmap) {
    return <div className='flex h-full w-full items-center justify-center text-muted-foreground'>Loading mindmap…</div>;
  }

  return (
    <ReactFlowProvider>
      <MindmapCanvasInner key={mindmap.id} mindmap={mindmap} />
    </ReactFlowProvider>
  );
}

export { MindmapCanvas as default };
